import { now } from "@@/shared/date-time/date-time";
import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";

const htmlToPdfCommandZodObject = z.object({
    _id: idZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("HTML_TO_PDF_COMMAND"),
    /** Url to html or a string containing the html to be rendered as pdf */
    html: z.string().url().or(z.string()),
    options: z
        .object({
            /**
             * Set to `false` to hide background graphics.
             * @defaultValue `true`
             */
            printBackground: z.boolean().default(true),
            /**
             * Whether the page should be in landscape orientation.
             * @defaultValue `false`
             */ landscape: z.boolean().default(false),
            /** @defaultValue `a4` */
            format: z
                .enum([
                    "letter",
                    "legal",
                    "tabloid",
                    "ledger",
                    "a0",
                    "a1",
                    "a2",
                    "a3",
                    "a4",
                    "a5",
                    "a6",
                ])
                .default("a4"),
            /**
             * Set the PDF margins.
             * @defaultValue `undefined` no margins are set.
             */
            margin: z.object({
                top: z.string(),
                right: z.string(),
                bottom: z.string(),
                left: z.string(),
            }),
            /**
             * Hides default white background and allows generating pdfs with transparency.
             * @defaultValue `false`
             */
            omitBackground: z.boolean().default(false),
            /**
             * HTML template for the print header. Should be valid HTML with the following
             * classes used to inject values into them:
             *
             * - `date` formatted print date
             *
             * - `title` document title
             *
             * - `url` document location
             *
             * - `pageNumber` current page number
             *
             * - `totalPages` total pages in the document
             */
            headerTemplate: z.string().optional(),
            /**
             * HTML template for the print footer. Should be valid HTML with the following
             * classes used to inject values into them:
             *
             * - `date` formatted print date
             *
             * - `title` document title
             *
             * - `url` document location
             *
             * - `pageNumber` current page number
             *
             * - `totalPages` total pages in the document
             */
            footerTemplate: z.string().optional(),
            /**
             * Whether to show the header and footer.
             * @defaultValue `false`
             */
            displayHeaderFooter: z.boolean().default(false),
            /**
             * Scales the rendering of the web page. Amount must be between `0.1` and `2`.
             * @defaultValue `1`
             */
            scale: z.number().min(0.1).max(2).default(1),
        })
        .optional(),
});

type HtmlToPdfCommand = z.infer<typeof htmlToPdfCommandZodObject>;

type FactoryParams = Except<HtmlToPdfCommand, "_id" | "_createdAt" | "_type">;
const htmlToPdfCommandFactory = (params: FactoryParams): HtmlToPdfCommand => {
    return {
        _id: generateId({ prefix: "html_to_pdf_command__" }),
        _createdAt: now(),
        _type: "HTML_TO_PDF_COMMAND",
        ...params,
    };
};

export { htmlToPdfCommandFactory, htmlToPdfCommandZodObject };
export type { HtmlToPdfCommand };
